import type { Resource } from '@medplum/fhirtypes';

import type {
  FhirView,
  ResourceOfType,
  ResourceTypeName,
} from './view-contract';

export type ComponentRegistration<
  K extends ResourceTypeName = ResourceTypeName,
> = {
  /** Stable registration identity. Re-registering the same id is an intentional override. */
  id: string;
  resourceType: K;
  /** Canonical profile URL, matched against `resource.meta.profile`. */
  profile?: string;
  component: FhirView<ResourceOfType<K>>;
};

type StoredRegistration = {
  id: string;
  resourceType: ResourceTypeName;
  profile?: string;
  component: FhirView;
};

/** Natural key of a registration's match target. */
function matchTuple(reg: Pick<StoredRegistration, 'resourceType' | 'profile'>) {
  return `${reg.resourceType}|${reg.profile ?? ''}`;
}

/**
 * Pure component dispatch: (resourceType, profile) → view. Carries no fetch
 * policy (the page's _include query owns expansion) and no view models —
 * views receive the typed resource directly.
 */
export class FhirComponentRegistry {
  private byId = new Map<string, StoredRegistration>();
  private byTuple = new Map<string, string>();
  private fallback: FhirView | undefined;

  register<K extends ResourceTypeName>(reg: ComponentRegistration<K>): this {
    const stored: StoredRegistration = {
      id: reg.id,
      resourceType: reg.resourceType,
      ...(reg.profile ? { profile: reg.profile } : {}),
      component: reg.component as FhirView,
    };
    const tuple = matchTuple(stored);
    const claimedBy = this.byTuple.get(tuple);
    if (claimedBy !== undefined && claimedBy !== stored.id) {
      throw new Error(
        `[fhir-renderer] component "${stored.id}" collides with "${claimedBy}" on match tuple (${tuple}); ` +
          `reuse the id to override intentionally, or disambiguate the match.`,
      );
    }
    const previous = this.byId.get(stored.id);
    if (previous) this.byTuple.delete(matchTuple(previous));
    this.byId.set(stored.id, stored);
    this.byTuple.set(tuple, stored.id);
    return this;
  }

  /** Component used when no registration matches the resource. */
  registerFallback(component: FhirView): this {
    this.fallback = component;
    return this;
  }

  /** Profile match outranks type match outranks fallback. */
  resolve(resource: Resource): FhirView | undefined {
    for (const profile of resource.meta?.profile ?? []) {
      const id = this.byTuple.get(`${resource.resourceType}|${profile}`);
      if (id) return this.byId.get(id)?.component;
    }
    const typeId = this.byTuple.get(`${resource.resourceType}|`);
    if (typeId) return this.byId.get(typeId)?.component;
    return this.fallback;
  }
}
